import React, { useContext, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { getAllPortalJobTypes } from "../../../../api/portalJobTypesApi";
import { getAllSections } from "../../../../api/projectEstimatesApi";
import {
  createEmptyTemplateItem,
  createEmptyTemplateSection,
  createProjectLevelTemplateSection,
  createScheduleTemplate,
  getScheduleTemplate,
  isProjectLevelTemplateSection,
  arrangeTemplateSections,
  updateScheduleTemplate,
} from "../../../../api/scheduleTemplatesApi";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import { splitSectionName } from "../projectsScheduleHelpers";
import { hasAdmsScheduleTemplatesAccess } from "../scheduleAccess";
import ProjectLevelGroupModal, {
  PlacementToggle,
} from "../ProjectLevelGroupModal";
import TemplateAddItemModal from "./TemplateAddItemModal";
import TemplateItemDrawer from "./TemplateItemDrawer";
import "../ProjectsSchedule.css";
import "../ProjectsScheduleDetail.css";
import "./ScheduleTemplates.css";

const BUILDING_TYPE_OPTIONS = [
  { value: "", label: "Любой тип" },
  { value: "МЖК", label: "МЖК" },
  { value: "ОЗ", label: "ОЗ" },
  { value: "ИЖД", label: "ИЖД" },
  { value: "НП16", label: "НП16" },
];

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: 44,
    borderRadius: 10,
    borderColor: "#d1d5db",
    boxShadow: "none",
    fontSize: 14,
    "&:hover": { borderColor: "#9ca3af" },
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 20,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1100,
  }),
};

const reindexItems = (items) =>
  items.map((item, index) => ({ ...item, order_index: index }));

const ScheduleTemplateEditor = () => {
  const { templateId } = useParams();
  const isNew = !templateId;
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const isAdms = hasAdmsScheduleTemplatesAccess(userData);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [buildingType, setBuildingType] = useState("");
  const [sections, setSections] = useState([]);
  const [catalogSections, setCatalogSections] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [addSectionId, setAddSectionId] = useState(null);
  const [itemModal, setItemModal] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [groupModal, setGroupModal] = useState(null);

  useEffect(() => {
    if (!isAdms) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const [catalog, portalJobTypes] = await Promise.all([
          getAllSections(),
          getAllPortalJobTypes({ is_active: true }).catch(() => []),
        ]);
        if (cancelled) return;
        const list = Array.isArray(catalog)
          ? catalog
          : catalog?.items || catalog?.sections || [];
        setCatalogSections(list.filter((item) => item?.id && item?.name));
        setJobTypes(Array.isArray(portalJobTypes) ? portalJobTypes : []);
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Не удалось загрузить справочник разделов");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdms]);

  useEffect(() => {
    if (!isAdms || isNew) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getScheduleTemplate(templateId);
        if (cancelled) return;
        setName(data.name || "");
        setDescription(data.description || "");
        setBuildingType(data.building_type || "");
        const nextSections = Array.isArray(data.sections) ? data.sections : [];
        setSections(arrangeTemplateSections(nextSections));
        setExpandedIds(new Set(nextSections.map((section) => section.id)));
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Не удалось загрузить шаблон");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAdms, isNew, templateId]);

  useEffect(() => {
    if (!catalogSections.length) return;
    setSections((prev) => {
      let changed = false;
      const next = prev.map((section) => {
        const catalog = catalogSections.find(
          (item) => Number(item.id) === Number(section.catalog_section_id),
        );
        if (
          isProjectLevelTemplateSection(section) ||
          !catalog?.name ||
          catalog.name === section.name
        ) {
          return section;
        }
        changed = true;
        return { ...section, name: catalog.name };
      });
      return changed ? next : prev;
    });
  }, [catalogSections, templateId, loading]);

  const catalogOptions = useMemo(() => {
    const used = new Set(
      sections
        .map((section) => Number(section.catalog_section_id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    return catalogSections
      .filter((section) => !used.has(Number(section.id)))
      .map((section) => ({
        value: section.id,
        label: section.name,
        raw: section,
      }));
  }, [catalogSections, sections]);

  const toggleExpand = (sectionId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleAddSection = (option) => {
    if (!option?.raw) return;
    const nextSection = {
      ...createEmptyTemplateSection(option.raw),
      order_index: sections.length,
    };
    setSections((prev) => arrangeTemplateSections([...prev, nextSection]));
    setExpandedIds((prev) => new Set(prev).add(nextSection.id));
    setAddSectionId(null);
  };

  const handleAddProjectLevel = () => {
    setGroupModal({ mode: "create" });
  };

  const handleSubmitProjectLevelGroup = ({ name, placement, group }) => {
    if (group) {
      setSections((prev) =>
        arrangeTemplateSections(
          prev.map((section) =>
            section.id === group.id
              ? { ...section, name, placement }
              : section,
          ),
        ),
      );
      setGroupModal(null);
      return;
    }
    const nextSection = createProjectLevelTemplateSection({ name, placement });
    setSections((prev) => arrangeTemplateSections([...prev, nextSection]));
    setExpandedIds((prev) => new Set(prev).add(nextSection.id));
    setGroupModal(null);
  };

  const handleProjectLevelPlacement = (section, placement) => {
    setSections((prev) =>
      arrangeTemplateSections(
        prev.map((item) =>
          item.id === section.id ? { ...item, placement } : item,
        ),
      ),
    );
  };

  const handleRemoveSection = (sectionId) => {
    const removedIds = new Set(
      (sections.find((section) => section.id === sectionId)?.items || []).map(
        (item) => String(item.id),
      ),
    );
    setSections((prev) =>
      prev
        .filter((section) => section.id !== sectionId)
        .map((section, index) => ({
          ...section,
          order_index: index,
          items: (section.items || []).map((item) => ({
            ...item,
            predecessor_ids: (item.predecessor_ids || []).filter(
              (id) => !removedIds.has(String(id)),
            ),
            successor_ids: (item.successor_ids || []).filter(
              (id) => !removedIds.has(String(id)),
            ),
          })),
        })),
    );
  };

  const handleRemoveItem = (itemId) => {
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        items: reindexItems(
          (section.items || [])
            .filter((item) => String(item.id) !== String(itemId))
            .map((item) => ({
              ...item,
              predecessor_ids: (item.predecessor_ids || []).filter(
                (id) => String(id) !== String(itemId),
              ),
              successor_ids: (item.successor_ids || []).filter(
                (id) => String(id) !== String(itemId),
              ),
            })),
        ),
      })),
    );
    setEditingItem(null);
  };

  const handleSaveItem = (updated) => {
    setSections((prev) =>
      prev.map((section) => ({
        ...section,
        items: (section.items || []).map((item) => {
          if (String(item.id) === String(updated.id)) {
            return { ...item, ...updated };
          }
          const predecessor_ids = (item.predecessor_ids || []).filter(
            (id) => String(id) !== String(updated.id),
          );
          const successor_ids = (item.successor_ids || []).filter(
            (id) => String(id) !== String(updated.id),
          );
          if (
            (updated.successor_ids || []).some(
              (id) => String(id) === String(item.id),
            )
          ) {
            predecessor_ids.push(updated.id);
          }
          if (
            (updated.predecessor_ids || []).some(
              (id) => String(id) === String(item.id),
            )
          ) {
            successor_ids.push(updated.id);
          }
          return { ...item, predecessor_ids, successor_ids };
        }),
      })),
    );
  };

  const updateSectionItems = (sectionId, updater) => {
    setSections((prev) =>
      prev.map((section) => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          items: reindexItems(updater(section.items || [])),
        };
      }),
    );
  };

  const handleAddItems = (names) => {
    if (!itemModal) return;
    updateSectionItems(itemModal.sectionId, (items) => {
      const existing = new Set(
        items.map((item) => String(item.name || "").trim().toLowerCase()),
      );
      const next = [...items];
      names.forEach((name) => {
        if (!name || existing.has(name.toLowerCase())) return;
        existing.add(name.toLowerCase());
        next.push(
          createEmptyTemplateItem({
            name,
            isMilestone: itemModal.isMilestone,
            orderIndex: next.length,
          }),
        );
      });
      return next;
    });
  };

  const moveItem = (sectionId, itemId, direction) => {
    updateSectionItems(sectionId, (items) => {
      const index = items.findIndex((item) => item.id === itemId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= items.length) return items;
      const next = [...items];
      const [moved] = next.splice(index, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Укажите название шаблона");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        name: name.trim(),
        description: description.trim(),
        building_type: buildingType || null,
        sections: arrangeTemplateSections(
          sections.map((section, index) => ({
            ...section,
            order_index: index,
            items: reindexItems(section.items || []).map((item) => ({
              ...item,
              predecessor_ids: item.predecessor_ids || [],
              successor_ids: item.successor_ids || [],
            })),
          })),
        ),
      };
      if (isNew) {
        await createScheduleTemplate(payload);
      } else {
        await updateScheduleTemplate(templateId, payload);
      }
      navigate("/projects/schedule/templates");
    } catch (e) {
      setError(e?.message || "Не удалось сохранить шаблон");
    } finally {
      setSaving(false);
    }
  };

  if (!isAdms) {
    return (
      <div className="ps-page">
        <div className="ps-state ps-state--error">
          Раздел доступен только пользователям с уровнем доступа АДМС.
        </div>
      </div>
    );
  }

  const suggestedNamesForSection = (section, isMilestone) => {
    const isProjectLevel = isProjectLevelTemplateSection(section);
    if (isProjectLevel) {
      const seen = new Set();
      const names = [];
      sections.forEach((item) => {
        if (isProjectLevelTemplateSection(item)) return;
        (Array.isArray(item.items) ? item.items : []).forEach((workType) => {
          if (Boolean(workType?.is_milestone) !== Boolean(isMilestone)) return;
          const name = String(workType?.name || "").trim();
          const key = name.toLowerCase();
          if (!name || seen.has(key)) return;
          seen.add(key);
          names.push(name);
        });
      });
      return names;
    }
    const catalogId = Number(section.catalog_section_id);
    return jobTypes
      .filter((job) => {
        if (Number(job.project_section_id) !== catalogId) return false;
        const name = String(job.name || "").toLowerCase();
        const looksLikeMilestone =
          name.includes("веха") || name.includes("milestone");
        return isMilestone ? looksLikeMilestone : !looksLikeMilestone;
      })
      .map((job) => job.name)
      .filter(Boolean);
  };

  return (
    <div className="ps-page pst-page">
      <div className="psd-header pst-header">
        <button
          type="button"
          className="psd-back"
          onClick={() => navigate("/projects/schedule/templates")}
          aria-label="Назад к списку шаблонов"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="psd-title">
          {isNew ? "Создание шаблона графика" : "Редактирование шаблона"}
        </h1>
        <button
          type="button"
          className="pst-primary-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Сохранение..." : "Сохранить шаблон"}
        </button>
      </div>

      {loading ? (
        <div className="ps-state">Загрузка...</div>
      ) : (
        <>
          <div className="ps-table-card pst-editor-card">
            <div className="pst-editor-grid">
              <label className="pst-field">
                <span>Название шаблона</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Например, МЖК — типовой график РП"
                />
              </label>
              <label className="pst-field">
                <span>Тип здания</span>
                <Select
                  styles={selectStyles}
                  options={BUILDING_TYPE_OPTIONS}
                  value={
                    BUILDING_TYPE_OPTIONS.find(
                      (opt) => opt.value === buildingType,
                    ) || BUILDING_TYPE_OPTIONS[0]
                  }
                  onChange={(opt) => setBuildingType(opt?.value || "")}
                />
              </label>
              <label className="pst-field pst-field--wide">
                <span>Описание</span>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Кратко, для какого вида проектов этот шаблон"
                />
              </label>
            </div>
            <p className="pst-editor-note">
              В шаблоне задаются виды работ, вехи, удельный вес, порядок и связи.
              Сроки пользователь проставляет уже на проекте. При необходимости
              можно отдельно добавить работы вне разделов: они не входят в %
              завершения РП, но могут быть связаны с разделами.
            </p>
          </div>

          {error && <div className="ps-state ps-state--error">{error}</div>}

          <div className="ps-table-card pst-editor-card">
            <div className="pst-section-add">
              <div className="pst-section-add__select">
                <Select
                  styles={selectStyles}
                  options={catalogOptions}
                  value={
                    catalogOptions.find((opt) => opt.value === addSectionId) ||
                    null
                  }
                  onChange={(opt) => setAddSectionId(opt?.value || null)}
                  placeholder="Выберите раздел для шаблона..."
                  noOptionsMessage={() =>
                    catalogOptions.length === 0
                      ? "Все разделы уже добавлены"
                      : "Разделы не найдены"
                  }
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <button
                type="button"
                className="pst-primary-btn"
                disabled={!addSectionId}
                onClick={() =>
                  handleAddSection(
                    catalogOptions.find((opt) => opt.value === addSectionId),
                  )
                }
              >
                Добавить раздел
              </button>
              <button
                type="button"
                className="pst-secondary-btn"
                onClick={handleAddProjectLevel}
              >
                Добавить вне разделов
              </button>
            </div>
          </div>

          <div className="ps-table-card psd-sections-card">
            <div className="ps-table-wrap">
              <table className="ps-table psd-sections-table">
                <thead>
                  <tr>
                    <th>Раздел шаблона</th>
                    <th>Работы</th>
                    <th>Вехи</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="ps-state">
                        Добавьте разделы — затем накиньте виды работ и вехи
                      </td>
                    </tr>
                  ) : (
                    sections.map((section) => {
                      const isProjectLevel =
                        isProjectLevelTemplateSection(section);
                      const { code, title } = splitSectionName(section.name);
                      const isExpanded = expandedIds.has(section.id);
                      const items = [...(section.items || [])].sort(
                        (a, b) =>
                          (a.order_index ?? 0) - (b.order_index ?? 0),
                      );
                      const works = items.filter((item) => !item.is_milestone);
                      const milestones = items.filter((item) => item.is_milestone);

                      return (
                        <React.Fragment key={section.id}>
                          <tr
                            className={`psd-section-row${
                              isProjectLevel ? " psd-section-row--project" : ""
                            }${
                              isExpanded ? " psd-section-row--expanded" : ""
                            }`}
                          >
                            <td>
                              <div className="psd-section-name">
                                <button
                                  type="button"
                                  className="psd-expand"
                                  onClick={() => toggleExpand(section.id)}
                                  aria-expanded={isExpanded}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className={
                                      isExpanded ? "psd-expand__icon--open" : ""
                                    }
                                  >
                                    <path
                                      d="M9 6l6 6-6 6"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                                <div className="psd-section-name__text">
                                  {isProjectLevel ? (
                                    <div className="psd-section-name__plain">
                                      {section.name}
                                    </div>
                                  ) : (
                                    <>
                                      <div className="psd-section-name__code">
                                        {code}
                                      </div>
                                      {title && (
                                        <div className="psd-section-name__title">
                                          {title}
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {isProjectLevel && (
                                    <div className="psd-project-level-note">
                                      не входят в % завершения РП
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>{works.length}</td>
                            <td>{milestones.length}</td>
                            <td>
                              <div className="psd-plg-actions">
                                {isProjectLevel && (
                                  <>
                                    <PlacementToggle
                                      value={section.placement}
                                      onChange={(placement) =>
                                        handleProjectLevelPlacement(
                                          section,
                                          placement,
                                        )
                                      }
                                    />
                                    <button
                                      type="button"
                                      className="psd-toolbar-btn psd-toolbar-btn--ghost"
                                      onClick={() =>
                                        setGroupModal({
                                          mode: "edit",
                                          group: section,
                                        })
                                      }
                                    >
                                      Переименовать
                                    </button>
                                  </>
                                )}
                                <button
                                  type="button"
                                  className="pst-danger-btn"
                                  onClick={() => handleRemoveSection(section.id)}
                                >
                                  Удалить
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="psd-expand-row">
                              <td colSpan={4}>
                                <div className="psd-expand-panel">
                                  <div className="psd-expand-panel__header">
                                    <h3 className="psd-expand-panel__title">
                                      Виды работ и вехи
                                    </h3>
                                    <div className="psd-expand-panel__actions">
                                      <button
                                        type="button"
                                        className="psd-add-btn"
                                        onClick={() =>
                                          setItemModal({
                                            sectionId: section.id,
                                            isMilestone: false,
                                          })
                                        }
                                      >
                                        + Работа
                                      </button>
                                      <button
                                        type="button"
                                        className="psd-add-btn psd-add-btn--milestone"
                                        onClick={() =>
                                          setItemModal({
                                            sectionId: section.id,
                                            isMilestone: true,
                                          })
                                        }
                                      >
                                        ◆ Веха
                                      </button>
                                    </div>
                                  </div>

                                  <div className="psd-nested-table-wrap">
                                    <table className="psd-nested-table">
                                      <thead>
                                        <tr>
                                          <th>Порядок</th>
                                          <th>Наименование</th>
                                          <th>Тип</th>
                                          <th>Удельный вес</th>
                                          <th>Связи</th>
                                          <th>Действия</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {items.length === 0 ? (
                                          <tr>
                                            <td
                                              colSpan={6}
                                              className="psd-expand-panel__state"
                                            >
                                              Пока пусто — добавьте работы и вехи
                                            </td>
                                          </tr>
                                        ) : (
                                          items.map((item, index) => {
                                            const predCount = (
                                              item.predecessor_ids || []
                                            ).length;
                                            const succCount = (
                                              item.successor_ids || []
                                            ).length;
                                            const relationsLabel =
                                              predCount || succCount
                                                ? `${predCount} пред. / ${succCount} посл.`
                                                : "—";

                                            return (
                                            <tr
                                              key={item.id}
                                              className={`psd-wt-row--clickable${
                                                item.is_milestone
                                                  ? " psd-row--milestone"
                                                  : ""
                                              }${
                                                editingItem?.item?.id === item.id
                                                  ? " psd-wt-row--active"
                                                  : ""
                                              }`}
                                              onClick={() =>
                                                setEditingItem({ item, section })
                                              }
                                            >
                                              <td>
                                                <div className="pst-order-btns">
                                                  <button
                                                    type="button"
                                                    disabled={index === 0}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      moveItem(
                                                        section.id,
                                                        item.id,
                                                        -1,
                                                      );
                                                    }}
                                                    aria-label="Выше"
                                                  >
                                                    ↑
                                                  </button>
                                                  <button
                                                    type="button"
                                                    disabled={
                                                      index === items.length - 1
                                                    }
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      moveItem(
                                                        section.id,
                                                        item.id,
                                                        1,
                                                      );
                                                    }}
                                                    aria-label="Ниже"
                                                  >
                                                    ↓
                                                  </button>
                                                </div>
                                              </td>
                                              <td>
                                                {item.is_milestone && (
                                                  <span
                                                    className="psd-milestone-icon"
                                                    aria-hidden
                                                  >
                                                    ◆
                                                  </span>
                                                )}{" "}
                                                {item.name}
                                              </td>
                                              <td>
                                                <span
                                                  className={`psd-type-badge${
                                                    item.is_milestone
                                                      ? " psd-type-badge--milestone"
                                                      : ""
                                                  }`}
                                                >
                                                  {item.is_milestone
                                                    ? "Веха"
                                                    : "Работа"}
                                                </span>
                                              </td>
                                              <td>
                                                {item.is_milestone || isProjectLevel ? (
                                                  "—"
                                                ) : (
                                                  <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    className="pst-weight-input"
                                                    value={
                                                      item.unit_weight_percent ??
                                                      0
                                                    }
                                                    onClick={(e) =>
                                                      e.stopPropagation()
                                                    }
                                                    onChange={(e) => {
                                                      const value = Number(
                                                        e.target.value,
                                                      );
                                                      updateSectionItems(
                                                        section.id,
                                                        (current) =>
                                                          current.map((row) =>
                                                            row.id === item.id
                                                              ? {
                                                                  ...row,
                                                                  unit_weight_percent:
                                                                    Number.isFinite(
                                                                      value,
                                                                    )
                                                                      ? value
                                                                      : 0,
                                                                }
                                                              : row,
                                                          ),
                                                      );
                                                    }}
                                                  />
                                                )}
                                              </td>
                                              <td>{relationsLabel}</td>
                                              <td>
                                                <button
                                                  type="button"
                                                  className="pst-danger-btn"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveItem(item.id);
                                                  }}
                                                >
                                                  Удалить
                                                </button>
                                              </td>
                                            </tr>
                                            );
                                          })
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <TemplateAddItemModal
        open={Boolean(itemModal)}
        isMilestone={Boolean(itemModal?.isMilestone)}
        existingNames={
          sections
            .find((section) => section.id === itemModal?.sectionId)
            ?.items?.filter(
              (item) => Boolean(item.is_milestone) === Boolean(itemModal?.isMilestone),
            )
            .map((item) => item.name) || []
        }
        suggestedNames={
          itemModal
            ? suggestedNamesForSection(
                sections.find((section) => section.id === itemModal.sectionId) ||
                  {},
                itemModal.isMilestone,
              )
            : []
        }
        onClose={() => setItemModal(null)}
        onAdd={handleAddItems}
      />

      <TemplateItemDrawer
        open={Boolean(editingItem)}
        item={
          sections
            .flatMap((section) =>
              (section.items || []).map((item) => ({ item, section })),
            )
            .find((row) => row.item.id === editingItem?.item?.id)?.item ||
          editingItem?.item
        }
        section={
          sections.find(
            (section) =>
              section.id === editingItem?.section?.id ||
              (section.items || []).some(
                (item) => item.id === editingItem?.item?.id,
              ),
          ) || editingItem?.section
        }
        sections={sections}
        onClose={() => setEditingItem(null)}
        onSave={handleSaveItem}
        onDelete={(item) => handleRemoveItem(item.id)}
      />

      <ProjectLevelGroupModal
        key={groupModal ? `${groupModal.mode}-${groupModal.group?.id || "new"}` : "closed"}
        open={Boolean(groupModal)}
        group={groupModal?.group || null}
        onClose={() => setGroupModal(null)}
        onSubmit={handleSubmitProjectLevelGroup}
      />
    </div>
  );
};

export default ScheduleTemplateEditor;
